import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role === "customer") { setError("This login is for staff only."); setBusy(false); return; }
      toast.success(`Welcome, ${u.name || "team"}`);
      navigate(u.role === "admin" ? "/admin" : "/driver");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen zr-grid-bg flex flex-col items-center justify-center px-4">
      <Link to="/" className="mb-8"><Brand size="lg" /></Link>
      <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-[#12141C]/90 p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center gap-2 text-[#CCFF00]"><Shield className="h-5 w-5" /><span className="font-heading font-bold uppercase tracking-wide">Staff Access</span></div>
        <h1 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-white">Sign In</h1>
        <p className="mt-1 text-sm text-zinc-400">Admin & Driver access only.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-zinc-300">Email</Label>
            <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-1 bg-zinc-900 border-zinc-700 text-white" placeholder="you@email.com" />
          </div>
          <div>
            <Label className="text-zinc-300">Password</Label>
            <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="mt-1 bg-zinc-900 border-zinc-700 text-white" placeholder="••••••••" />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
          <Button data-testid="login-submit-btn" type="submit" disabled={busy}
            className="w-full bg-[#CCFF00] hover:bg-[#d4ff33] text-black font-bold tracking-wide active:scale-95 transition-transform">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>
        <Link to="/" data-testid="back-store-link" className="mt-5 block text-center text-sm text-zinc-400 hover:text-white">← Back to store</Link>
      </div>
    </div>
  );
}
